import React, { createContext, useContext, useEffect, useState } from 'react'
import { ROLE_PERMISSIONS, type UserRole, type Permission } from '../constants/rolePermissions'

export type { UserRole, Permission }

export interface User {
  id: string
  phone: string
  email?: string
  name: string
  role: UserRole
  department?: string
  permissions: Permission[]
  lastAccess?: Date
  appType: 'gausakhi' | 'biogassangh' | 'urjavyapar' | 'urjasanyojak' | 'admin' | 'urjaneta' | 'shuddhidoot'
  executiveLevel?: 'ceo' | 'cfo' | 'coo' | 'vp' | 'director'
  // Backend response fields
  externalId?: string
  locale?: string
  kycStatus?: string
  isActive?: boolean
  roles?: string[]
  governmentAccess?: any[]
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (phone: string, password: string) => Promise<void>
  logout: () => Promise<void>
  hasPermission: (module: string, action: string) => boolean
  isExecutive: () => boolean
  getAccessLevel: () => 'high' | 'medium' | 'low'
  accessToken?: string
  refreshToken?: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  console.log('🔍 [AuthContext] Render - user:', user ? `${user.name} (${user.role})` : 'null', '| loading:', loading)

  // Enhanced permission checking
  const hasPermission = (module: string, action: string): boolean => {
    if (!user) return false

    const userPermissions = user.permissions || ROLE_PERMISSIONS[user.role] || []
    const modulePermissions = userPermissions.find(p => p.module === module)
    return modulePermissions?.actions.includes(action) || false
  }

  // Check if user has executive-level access
  const isExecutive = (): boolean => {
    if (!user) return false
    return user.role === 'senior_leader' || user.role === 'admin' || !!user.executiveLevel
  }

  // Get user access level for security controls
  const getAccessLevel = (): 'high' | 'medium' | 'low' => {
    if (!user) return 'low'

    if (user.executiveLevel) {
      return user.executiveLevel === 'ceo' || user.executiveLevel === 'cfo' ? 'high' : 'medium'
    }

    switch (user.role) {
      case 'senior_leader':
      case 'admin':
        return 'high'
      case 'cbg_sales':
      case 'business_dev':
        return 'medium'
      default:
        return 'low'
    }
  }

  // Backend authentication with JWT microservices integration
  const login = async (phone: string, password: string) => {
    try {
      console.log('🔐 Attempting login with SAUBHAGYA microservices API...')

      // Import the API service dynamically to avoid circular dependencies
      const { apiService } = await import('../services/api')

      // Send phone directly to API service (no email conversion needed)
      const loginResponse = await apiService.login(phone, password)

      if (loginResponse.success && loginResponse.data) {
        const { user: apiUser, token } = loginResponse.data

        console.log('📦 API Response - Raw User Data:', {
          id: apiUser.id,
          phone: apiUser.phone,
          name: apiUser.name,
          nameExists: 'name' in apiUser,
          nameIsEmpty: !apiUser.name || apiUser.name.trim() === '',
          role: apiUser.role,
          roles: apiUser.roles,
          email: apiUser.email
        })

        // CRITICAL: User name from API is the AUTHORITATIVE source
        if (!apiUser.name || apiUser.name.trim() === '') {
          console.error('❌ [CRITICAL] API returned user WITHOUT name field! This is a backend issue.')
          console.error('User data from API:', apiUser)
          throw new Error('Backend returned user without name field')
        }

        // Map API user to our User interface
        // Get the first role from the backend roles array
        const backendRole = apiUser.role || (apiUser.roles && apiUser.roles[0]) || 'USER'

        console.log('🔄 Mapping backend role:', backendRole)

        // Map role using ONLY the backend role, not phone-based fallback
        // (phone-based fallback is ONLY for demo credentials)
        const correctRole = mapBackendRoleToUserRole(backendRole)
        const correctAppType = getAppTypeFromRole(backendRole)

        console.log('✅ Mapped role:', correctRole, '| App type:', correctAppType)

        const mappedUser: User = {
          id: apiUser.id,
          phone: phone,
          email: apiUser.email,
          name: apiUser.name,  // CRITICAL: Use EXACTLY what API returned, no fallback
          role: correctRole,
          department: getDepartmentFromRole(backendRole),
          permissions: mapApiPermissions(apiUser.permissions || []),
          lastAccess: new Date(),
          appType: correctAppType,
          executiveLevel: getExecutiveLevelFromRole(backendRole),
          // API fields
          isActive: true,
          roles: apiUser.roles || [backendRole]
        }

        console.log('👤 Final Mapped User:', {
          name: mappedUser.name,
          phone: mappedUser.phone,
          role: mappedUser.role,
          appType: mappedUser.appType,
          displayName: `${mappedUser.name} (${mappedUser.phone})`
        })

        setUser(mappedUser)

        // Store tokens and user data
        localStorage.setItem('user', JSON.stringify(mappedUser))
        localStorage.setItem('saubhagya_jwt_token', token)
        localStorage.setItem('sessionStart', new Date().toISOString())

        console.log('✅ User logged in successfully with JWT - Full User:', mappedUser)

        // VALIDATION: Confirm name is still present in mappedUser before returning
        if (!mappedUser.name || mappedUser.name.trim() === '') {
          console.error('❌ [CRITICAL] Mapped user has no valid name after processing!')
          throw new Error('User data validation failed - name missing')
        }

        console.log('✅ [VALIDATION PASSED] User name confirmed in final state:', mappedUser.name)
        return

      } else {
        console.warn('🚫 Backend authentication failed, falling back to demo credentials')
        throw new Error(loginResponse.error || 'Authentication failed')
      }

    } catch (error) {
      console.error('💥 Backend authentication failed:', error)
      console.error('⚠️ NOT attempting fallback for real users - only demo credentials are supported in fallback mode')

      // Only attempt fallback for KNOWN demo phone numbers
      // Real users created in admin should get a proper error, not silent fallback
      const demoPhones = ['+919876543210', '+918765432109', '+917654321098', '+915432109876', '+913210987654', '+912109876543', '+919988776644']

      if (demoPhones.includes(phone)) {
        console.warn('⚠️ Attempting fallback for known demo phone number:', phone)
        return loginWithFallback(phone, password)
      } else {
        console.error('❌ Real user login failed - backend service appears to be unavailable')
        console.error('💡 If this is a user created in admin, ensure auth service is running on port 8081')
        throw new Error('Authentication service is unavailable. Please try again or contact support.')
      }
    }
  }

  // Fallback authentication for demo purposes ONLY
  // WARNING: This should ONLY allow hardcoded demo credentials to login
  // Real users created in admin should NOT reach this function if API is working
  const loginWithFallback = async (phone: string, password: string) => {
    console.warn('⚠️ [FALLBACK MODE] Backend authentication failed - attempting demo credentials')
    console.warn('⚠️ This fallback should ONLY work for hardcoded demo users, NOT real users from database')

    // Demo credentials with their expected passwords
    const demoCredentials: Record<string, { user: User; validPasswords: string[] }> = {
      '+919876543210': {
        user: {
          id: 'fw-001',
          phone: '+919876543210',
          email: 'fieldworker@saubhagya.com',
          name: 'Ramesh Kumar (Field Worker)',
          role: 'field_worker',
          department: 'Field Operations',
          appType: 'gausakhi',
          permissions: ROLE_PERMISSIONS.field_worker,
          lastAccess: new Date()
        },
        validPasswords: ['password123', 'fieldworker123']
      },
      '+918765432109': {
        user: {
          id: 'cm-001',
          phone: '+918765432109',
          email: 'clustermanager@saubhagya.com',
          name: 'Suresh Patel (Cluster Manager)',
          role: 'cluster_manager',
          department: 'Cluster Operations',
          appType: 'biogassangh',
          permissions: ROLE_PERMISSIONS.cluster_manager,
          lastAccess: new Date()
        },
        validPasswords: ['password123', 'admin123']
      },
      '+917654321098': {
        user: {
          id: 'cs-001',
          phone: '+917654321098',
          email: 'cbgsales@saubhagya.com',
          name: 'Priya Sharma (CBG Sales Executive)',
          role: 'cbg_sales',
          department: 'Sales & Marketing',
          appType: 'urjavyapar',
          permissions: ROLE_PERMISSIONS.cbg_sales,
          lastAccess: new Date()
        },
        validPasswords: ['password123', 'admin123']
      },
      '+915432109876': {
        user: {
          id: 'admin-001',
          phone: '+915432109876',
          email: 'admin@saubhagya.com',
          name: 'System Administrator',
          role: 'admin',
          department: 'IT Administration',
          appType: 'admin',
          permissions: ROLE_PERMISSIONS.admin,
          lastAccess: new Date()
        },
        validPasswords: ['password123', 'admin123']
      },
      '+913210987654': {
        user: {
          id: 'po-001',
          phone: '+913210987654',
          email: 'purificationop@saubhagya.com',
          name: 'Vinod Yadav (Purification Operator)',
          role: 'purification_operator',
          department: 'Purification Operations',
          appType: 'shuddhidoot',
          permissions: ROLE_PERMISSIONS.purification_operator,
          lastAccess: new Date()
        },
        validPasswords: ['password123', 'admin123']
      },
      '+912109876543': {
        user: {
          id: 't-001',
          phone: '+912109876543',
          email: 'transporter@saubhagya.com',
          name: 'Ravi Sharma (Transporter)',
          role: 'transporter',
          department: 'Logistics & Transport',
          appType: 'admin',
          permissions: ROLE_PERMISSIONS.transporter,
          lastAccess: new Date()
        },
        validPasswords: ['password123', 'admin123']
      },
      '+919988776644': {
        user: {
          id: 'test-user-001',
          phone: '+919988776644',
          email: 'test@saubhagya.com',
          name: 'Test User',
          role: 'cluster_manager',
          department: 'Cluster Operations',
          appType: 'biogassangh',
          permissions: ROLE_PERMISSIONS.cluster_manager,
          lastAccess: new Date()
        },
        validPasswords: ['password123', 'admin123', 'Farmer@123', 'Test@123']
      }
    }

    // List available demo credentials for debugging
    const availableDemoPhones = Object.keys(demoCredentials)
    console.log('📋 Available demo credentials:', availableDemoPhones)

    // Check if phone matches a demo credential
    const demoCredential = demoCredentials[phone]
    if (!demoCredential) {
      console.error('❌ Phone number not in demo credentials list:', phone)
      console.error('💡 This appears to be a real user created in admin - backend API must be working for this user to login')
      throw new Error('Backend authentication failed. Please ensure the auth service is running or contact support.')
    }

    // Verify password matches one of the valid passwords for this demo user
    if (!demoCredential.validPasswords.includes(password)) {
      console.error('❌ Invalid password for demo user:', phone)
      console.error('💡 Valid passwords for this demo user:', demoCredential.validPasswords)
      throw new Error('Invalid phone number or password')
    }

    // Allow demo login
    console.log('✅ [DEMO MODE] Logging in with hardcoded demo credentials')
    const mockUser = demoCredential.user
    setUser(mockUser)
    localStorage.setItem('user', JSON.stringify(mockUser))
    localStorage.setItem('sessionStart', new Date().toISOString())

    // CRITICAL FIX: Generate a properly signed JWT token for fallback mode users
    // This uses HmacSHA512 signature to match the backend's validation
    try {
      // Import jwt-encode for proper JWT signing
      const JwtEncode = (await import('jwt-encode')).default

      // Use the same secret key as the backend (from application.yml)
      const secretKey = 'saubhagya-jwt-secret-key-2024-very-long-and-secure-fallbacksaubhagya-jwt-secret-key-2024-very-long-and-secure-fallbacksaubhagya-jwt-secret-key-2024-very-long-and-secure-fallback'

      // Create JWT payload matching backend expectations
      const payload = {
        sub: mockUser.id,
        phone: mockUser.phone,
        name: mockUser.name,
        user_id: mockUser.id,
        roles: mockUser.roles || ['SALES_TEAM'],
        permissions: mockUser.permissions || [],
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + (24 * 60 * 60) // 24 hours
      }

      // Sign token with HS512 algorithm
      const demoToken = JwtEncode(payload, secretKey, 'HS512')

      // Store token WITHOUT Bearer prefix - API clients will add it when sending
      localStorage.setItem('saubhagya_jwt_token', demoToken)
      console.log('✅ Demo login successful:', mockUser.name)
      console.log('✅ Properly signed JWT token stored:', demoToken.substring(0, 50) + '...')
    } catch (error) {
      console.error('❌ Failed to generate JWT token:', error)
      // Fallback: Store token without signature (will likely fail but won't crash)
      const header = btoa(JSON.stringify({ alg: 'HS512', typ: 'JWT' }))
      const payload = btoa(JSON.stringify({
        sub: mockUser.id,
        phone: mockUser.phone,
        name: mockUser.name,
        roles: mockUser.roles || ['SALES_TEAM'],
        permissions: mockUser.permissions || [],
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + (24 * 60 * 60)
      }))
      const signature = btoa('fallback-demo-token')
      const fallbackToken = `${header}.${payload}.${signature}`
      localStorage.setItem('saubhagya_jwt_token', fallbackToken)
      console.warn('⚠️ Using fallback unsigned token - stored WITHOUT Bearer prefix')
    }
  }

  const logout = async () => {
    try {
      // Import the API service dynamically to avoid circular dependencies
      const { apiService } = await import('../services/api')
      await apiService.logout()
    } catch (error) {
      console.error('Backend logout error:', error)
    }

    console.log('🔓 Logging out user, clearing ALL session data')
    setUser(null)

    // Clear user-related localStorage entries
    localStorage.removeItem('user')
    localStorage.removeItem('saubhagya_jwt_token')
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('sessionStart')
    localStorage.removeItem('voiceSessionData')

    // CRITICAL: Clear cache validation keys to prevent stale data reuse
    localStorage.removeItem('currentLoginInfo')
    localStorage.removeItem('lastLoggedInUser')
    localStorage.removeItem('previousUserPhone')

    // Clear any other potentially cached user data
    localStorage.removeItem('currentUser')
    localStorage.removeItem('userProfile')
    localStorage.removeItem('userData')

    console.log('✅ [LOGOUT] All user session data cleared from localStorage')
  }

  useEffect(() => {
    console.log('🔍 [AuthContext] useEffect - Initializing from localStorage')
    // Check for existing user session with security validation
    const savedUser = localStorage.getItem('user')
    const sessionStart = localStorage.getItem('sessionStart')
    const token = localStorage.getItem('saubhagya_jwt_token')

    console.log('🔍 [AuthContext] localStorage check:', {
      hasUser: !!savedUser,
      hasSessionStart: !!sessionStart,
      hasToken: !!token
    })

    if (savedUser && sessionStart) {
      const parsedUser = JSON.parse(savedUser)
      const sessionAge = Date.now() - new Date(sessionStart).getTime()
      const maxSessionAge = parsedUser.role === 'executive' ? 4 * 60 * 60 * 1000 : 8 * 60 * 60 * 1000 // 4h for exec, 8h for others

      console.log('🔍 [AuthContext] Session check:', {
        user: parsedUser.name,
        sessionAge: Math.floor(sessionAge / 1000 / 60) + ' minutes',
        maxAge: Math.floor(maxSessionAge / 1000 / 60) + ' minutes',
        expired: sessionAge > maxSessionAge
      })

      if (sessionAge > maxSessionAge) {
        console.log('⏰ [AuthContext] Session expired, logging out')
        logout()
      } else {
        // CRITICAL FIX: Validate user name is not empty/null (prevents stale cache data)
        if (!parsedUser.name || parsedUser.name.trim() === '') {
          console.warn('⚠️ [AuthContext] Cached user has empty name - clearing invalid session')
          logout()
        } else {
          // Update permissions to latest role definition
          parsedUser.permissions = ROLE_PERMISSIONS[parsedUser.role] || []
          console.log('✅ [AuthContext] Restored user from localStorage:', parsedUser.name)
          setUser(parsedUser)
        }
      }
    } else {
      console.log('❌ [AuthContext] No saved session found in localStorage')
    }
    console.log('🔍 [AuthContext] Setting loading to false')
    setLoading(false)
  }, [])

  // Monitor executive sessions
  useEffect(() => {
    if (user && isExecutive()) {
      const interval = setInterval(() => {
        const sessionStart = localStorage.getItem('sessionStart')
        if (sessionStart) {
          const sessionAge = Date.now() - new Date(sessionStart).getTime()
          const warningTime = 3.5 * 60 * 60 * 1000 // Warn at 3.5 hours for executives

          if (sessionAge > warningTime && sessionAge < warningTime + 60000) {
            console.warn('Executive session will expire in 30 minutes')
          }
        }
      }, 60000) // Check every minute

      return () => clearInterval(interval)
    }
  }, [user])

  const value = {
    user,
    loading,
    login,
    logout,
    hasPermission,
    isExecutive,
    getAccessLevel,
    accessToken: localStorage.getItem('accessToken'),
    refreshToken: localStorage.getItem('refreshToken')
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Helper functions to map API data to frontend types
function mapApiRoleToUserRole(apiRole: string): UserRole {
  switch (apiRole.toLowerCase()) {
    case 'admin':
    case 'administrator':
      return 'admin'
    case 'field_worker':
    case 'farmer':
      return 'field_worker'
    case 'cluster_manager':
      return 'cluster_manager'
    case 'cbg_sales':
    case 'sales_executive':
      return 'cbg_sales'
    case 'business_dev':
    case 'business_developer':
      return 'business_dev'
    case 'senior_leader':
    case 'executive':
    case 'ceo':
    case 'cfo':
      return 'senior_leader'
    case 'purification_operator':
    case 'operator':
      return 'purification_operator'
    default:
      return 'admin'
  }
}

function mapApiPermissions(apiPermissions: string[]): Permission[] {
  // Map API permissions to our Permission interface
  const permissionMap: Record<string, Permission> = {
    'cattle:read': { module: 'rfid', actions: ['scan', 'sync'] },
    'cattle:write': { module: 'collection', actions: ['log', 'voice'] },
    'transactions:read': { module: 'disputes', actions: ['resolve', 'track'] },
    'dashboard:read': { module: 'voice_kpis', actions: ['view', 'analyze'] },
    'users:manage': { module: 'users', actions: ['create', 'read', 'update', 'delete'] }
  }

  const mappedPermissions: Permission[] = []
  apiPermissions.forEach(perm => {
    if (permissionMap[perm]) {
      mappedPermissions.push(permissionMap[perm])
    }
  })

  // If no permissions mapped, use role-based defaults
  if (mappedPermissions.length === 0) {
    return ROLE_PERMISSIONS.admin
  }

  return mappedPermissions
}

// Helper functions to map backend data to frontend types (legacy support)
function mapBackendRoleToUserRole(backendRole: string): UserRole {
  switch (backendRole) {
    case 'FARMER':
    case 'FIELD_WORKER':
      return 'field_worker'
    case 'CLUSTER_MANAGER':
    case 'BIOGAS_SANGH':
      return 'cluster_manager'
    case 'CBG_SALES':
    case 'SALES_EXECUTIVE':
      return 'cbg_sales'
    case 'BUSINESS_DEV':
    case 'BUSINESS_DEVELOPER':
      return 'business_dev'
    case 'ADMIN':
    case 'ADMINISTRATOR':
      return 'admin'
    case 'SENIOR_LEADER':
    case 'EXECUTIVE':
    case 'CEO':
    case 'CFO':
      return 'senior_leader'
    case 'PURIFICATION_OPERATOR':
    case 'OPERATOR':
      return 'purification_operator'
    default:
      return 'field_worker'
  }
}

function getDepartmentFromRole(backendRole: string): string {
  switch (backendRole) {
    case 'FARMER':
    case 'FIELD_WORKER':
      return 'Field Operations'
    case 'CLUSTER_MANAGER':
    case 'BIOGAS_SANGH':
      return 'Cluster Operations'
    case 'CBG_SALES':
    case 'SALES_EXECUTIVE':
      return 'Sales & Marketing'
    case 'BUSINESS_DEV':
    case 'BUSINESS_DEVELOPER':
      return 'Business Development'
    case 'ADMIN':
    case 'ADMINISTRATOR':
      return 'IT Administration'
    case 'SENIOR_LEADER':
    case 'EXECUTIVE':
    case 'CEO':
    case 'CFO':
      return 'Executive Leadership'
    case 'PURIFICATION_OPERATOR':
    case 'OPERATOR':
      return 'Purification Operations'
    default:
      return 'Field Operations'
  }
}

function getAppTypeFromRole(backendRole: string): User['appType'] {
  switch (backendRole) {
    case 'FARMER':
    case 'FIELD_WORKER':
      return 'gausakhi'
    case 'CLUSTER_MANAGER':
    case 'BIOGAS_SANGH':
      return 'biogassangh'
    case 'CBG_SALES':
    case 'SALES_EXECUTIVE':
      return 'urjavyapar'
    case 'BUSINESS_DEV':
    case 'BUSINESS_DEVELOPER':
      return 'urjasanyojak'
    case 'ADMIN':
    case 'ADMINISTRATOR':
      return 'admin'
    case 'SENIOR_LEADER':
    case 'EXECUTIVE':
    case 'CEO':
    case 'CFO':
      return 'urjaneta'
    case 'PURIFICATION_OPERATOR':
    case 'OPERATOR':
      return 'shuddhidoot'
    default:
      return 'gausakhi'
  }
}

function getExecutiveLevelFromRole(backendRole: string): User['executiveLevel'] {
  switch (backendRole) {
    case 'CEO':
      return 'ceo'
    case 'CFO':
      return 'cfo'
    case 'COO':
      return 'coo'
    case 'VP':
      return 'vp'
    case 'DIRECTOR':
      return 'director'
    default:
      return undefined
  }
}

function mapBackendPermissions(backendPermissions: string[]): Permission[] {
  // Map backend permissions to our Permission interface
  const permissionMap: Record<string, Permission> = {
    'CONTRIBUTE_BIOGAS': { module: 'rfid', actions: ['scan', 'sync'] },
    'VIEW_HISTORY': { module: 'collection', actions: ['log', 'voice'] },
    'RAISE_DISPUTE': { module: 'disputes', actions: ['resolve', 'track'] },
    'MANAGE_INVENTORY': { module: 'inventory', actions: ['manage', 'track'] },
    'CREATE_INVOICE': { module: 'invoicing', actions: ['create', 'send'] },
    'MANAGE_CRM': { module: 'crm', actions: ['manage', 'update'] },
    'ADMIN_ACCESS': { module: 'users', actions: ['create', 'read', 'update', 'delete'] },
    'VIEW_ANALYTICS': { module: 'voice_kpis', actions: ['view', 'analyze'] },
    'MONITOR_CH4': { module: 'ch4_monitoring', actions: ['monitor', 'alert'] }
  }

  const mappedPermissions: Permission[] = []
  backendPermissions.forEach(perm => {
    if (permissionMap[perm]) {
      mappedPermissions.push(permissionMap[perm])
    }
  })

  // If no permissions mapped, use role-based defaults
  if (mappedPermissions.length === 0) {
    return ROLE_PERMISSIONS.field_worker
  }

  return mappedPermissions
}

// Helper functions to map phone numbers to correct demo credentials
function getCorrectRoleForPhone(phone: string): UserRole | null {
  const phoneRoleMap: Record<string, UserRole> = {
    '+919876543210': 'field_worker',
    '+918765432109': 'cluster_manager',
    '+917654321098': 'cbg_sales',
    '+915432109876': 'admin',
    '+913210987654': 'purification_operator',
    '+912109876543': 'transporter'
  }
  return phoneRoleMap[phone] || null
}

function getCorrectAppTypeForPhone(phone: string): User['appType'] | null {
  const phoneAppTypeMap: Record<string, User['appType']> = {
    '+919876543210': 'gausakhi',
    '+918765432109': 'biogassangh',
    '+917654321098': 'urjavyapar',
    '+915432109876': 'admin',
    '+913210987654': 'shuddhidoot',
    '+912109876543': 'admin'
  }
  return phoneAppTypeMap[phone] || null
}
